import { styled } from "@mui/material";
import { baseUrl, getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { ActionButtons } from "./actions/buttons";
import {
    ButtonClickAnalyticsType,
    KmsTypeHeroProps,
    KmsTypeMediaSchedulingData,
    StatisticsIndicatorType,
} from "@kaltura/mediaspace-shared-types";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { Title } from "./title/Title";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { MediaPageComponentExtraProps } from "../MediaPageComponentExtraProps";

/**
 * see Kms_Type_Media_Title_Props
 */
export interface MediaTitleActionsProps {
    name: string;
    createdAt: number;
    showCreatedByIndicator?: boolean;
    updatedAt?: number;
    createdByType?: "createdAt" | "updatedAt";
    showViews?: boolean;
    views: number;
    statisticsIndicator?: StatisticsIndicatorType;
    displayName?: string;
    userId?: string;
    buttons: KmsTypeComponent[];
    menuItems: KmsTypeComponent[];
    schedulingData?: KmsTypeMediaSchedulingData;
}

export interface MediaTitleAreaProps extends Pick<KmsTypeHeroProps, "titleAlerts">, MediaPageComponentExtraProps {
    mediaTitleActionsProps: MediaTitleActionsProps;
    isEmptyRightPanel: boolean;
}

/**
 * Area below the player (or other media hero component) that contains:
 * - media title
 * - basic info (e.g. creation date)
 * - buttons (e.g. likes, comments)
 * - actions menu (e.g. edit, delete, publish)
 */
const StyledAreaContainer = styled(
    Box,
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "isEmptyRightPanel";
        },
    }
)<{ isEmptyRightPanel?: boolean }>(({ theme, isEmptyRightPanel }) => ({
    display: "flex",
    alignItems: "baseline",
    width: "100%",
    gap: theme.spacing(1, 3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down(isEmptyRightPanel ? theme.breakpoints.values.md : theme.breakpoints.values.lg)]: {
        flexDirection: "column",
    },
}));

const StyledTitleContainer = styled(Box)({
    flex: "1 0 0",
    minWidth: 0,
});

/**
 * Media title area
 */
export const MediaTitleArea = ({
    mediaTitleActionsProps,
    titleAlerts = [],
    isEmptyRightPanel,
    onRequestEntryReload,
}: MediaTitleAreaProps) => {
    const {
        name,
        createdAt,
        showCreatedByIndicator,
        updatedAt = createdAt,
        createdByType = "createdAt",
        showViews,
        views,
        statisticsIndicator,
        displayName,
        userId,
        buttons = [],
        menuItems = [],
        schedulingData,
    } = mediaTitleActionsProps;

    return (
        <div className={"kms-ds-media-page-title-area"}>
            {titleAlerts
                && titleAlerts.map((alert, index) => {
                    const { component, props } = alert;
                    const Alert = getComponentFromPath(component);
                    return <Alert key={index} {...props} />;
                })}

            <StyledAreaContainer isEmptyRightPanel={isEmptyRightPanel}>
                <StyledTitleContainer>
                    <Title
                        showCreatedByIndicator={!!showCreatedByIndicator}
                        createdByName={displayName}
                        createdByLink={baseUrl + "/createdby/" + userId}
                        entryName={name}
                        schedulingData={schedulingData}
                        createdAt={createdAt}
                        updatedAt={updatedAt}
                        createdByType={createdByType}
                        views={showViews ? views : undefined}
                        statisticsIndicator={statisticsIndicator ?? undefined}
                    />
                </StyledTitleContainer>
                {(buttons.length > 0 || menuItems.length > 0) && (
                    <Box>
                        <ActionButtons
                            buttons={buttons}
                            menu={{
                                items: menuItems,
                                analyticsData: {
                                    action: "Media Actions Menu",
                                    buttonType: ButtonClickAnalyticsType.MENU,
                                },
                            }}
                            onRequestEntryReload={onRequestEntryReload}
                        />
                    </Box>
                )}
            </StyledAreaContainer>
        </div>
    );
};
